// Black-Scholes pricing for European options (no dividends).

export const MULTIPLIER = 100;
const DAY_MS = 24 * 60 * 60 * 1000;

// Standard normal CDF (Abramowitz & Stegun 26.2.17, error < 7.5e-8).
export function normCdf(x) {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989422804014327 * Math.exp((-x * x) / 2);
  const p =
    d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return x > 0 ? 1 - p : p;
}

// S spot, K strike, T years, r rate (0.045), sigma vol (0.25), right "C" | "P".
export function bsPrice({ S, K, T, r, sigma, right }) {
  const intrinsic = right === "C" ? Math.max(S - K, 0) : Math.max(K - S, 0);
  if (!(S > 0) || !(K > 0) || !(T > 0) || !(sigma > 0)) return intrinsic;
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + (sigma * sigma) / 2) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  if (right === "C") return S * normCdf(d1) - K * Math.exp(-r * T) * normCdf(d2);
  return K * Math.exp(-r * T) * normCdf(-d2) - S * normCdf(-d1);
}

// Calendar days from `from` until 4pm ET-ish close on the expiry date (YYYY-MM-DD).
export function daysToExpiry(expiryDate, from = new Date()) {
  if (!expiryDate) return 0;
  const [y, m, d] = expiryDate.split("-").map(Number);
  const expiry = Date.UTC(y, m - 1, d, 20, 0, 0); // 16:00 America/New_York (EDT)
  return Math.max((expiry - from.getTime()) / DAY_MS, 0);
}

export function contractsFor(amount, entry) {
  if (!(amount > 0) || !(entry > 0)) return 0;
  return Math.max(1, Math.floor(amount / (entry * MULTIPLIER)));
}

// leg: { strike, right, entry, iv (decimal), contracts, dte (days) }
export function legValue(leg, S, { daysForward = 0, ivShift = 0, r = 0 }) {
  const T = Math.max(leg.dte - daysForward, 0) / 365;
  const sigma = Math.max(leg.iv + ivShift, 0.0001);
  const price = bsPrice({ S, K: leg.strike, T, r, sigma, right: leg.right });
  const cost = leg.entry * MULTIPLIER * leg.contracts;
  const value = price * MULTIPLIER * leg.contracts;
  return { price, cost, value, pnl: value - cost };
}
